import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {checkNullArr, checkNullStr, createTimeout, printError} from "../utils/ReactUtils";
import {Label} from "../index";
import {printLog} from "../../CommonUtils";

/**
 * FileDropZone Component
 *
 * A fully customizable component that allows users to drop files or click to upload files with specific extensions.
 * If a file with an unsupported extension is dropped or selected, an error message is displayed.
 *
 * @param {Object} props - The props object
 * @param {Array} props.allowedExtensions - An array of allowed file extensions (e.g., ['jpg', 'png', 'pdf'])
 * @param {string} props.label - The label text to display inside the drop zone
 * @param {function} props.onFileSelect - Callback function that gets called when a valid file is selected
 * @param {string} props.bgColor - Background color of the drop zone
 * @param {string} props.borderColor - Border color of the drop zone
 * @param {string} props.hoverBgColor - Background color when file is being dragged over the drop zone
 * @param {string} props.hoverBorderColor - Border color when file is being dragged over the drop zone
 *
 * @example
 * <FileDropZone
 *   className={''}
 *   allowedExtensions={['jpg', 'png']}
 *   label="Drag & drop a file here, or click to select one"
 *   onFileSelect={(event, file, fileName, fileExt) => console.log(file)}
 *   bgColor="#f0f0f0"
 *   borderColor="#ccc"
 *   labelColor="#ccc"
 *   fileNameLabelColor="#000000"
 *   hoverBgColor="#d0f0d0"
 *   hoverBorderColor="#4CAF50"
 * />
 */
const FileDropZone = React.memo(({
                                     className = '',
                                     allowedExtensions,
                                     label = 'Drag & drop a file here, or click to select one',
                                     bgColor = '#f0f0f0',
                                     labelColor = '#bbbbbb',
                                     fileNameLabelColor = '#000000',
                                     borderColor = '#ccc',
                                     hoverBgColor = '#d0f0d0',
                                     hoverBorderColor = '#4CAF50',
                                     onFileSelect,
                                 }) => {
    const TAG = 'FileDropZone';

    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState('');
    const [fileName, setFileName] = useState('');
    const fileChooserInputRef = useRef(null);

    useEffect(() => {
        if (checkNullStr(error)) {
            // log('useEffect.error:', error);
            createTimeout(() => {
                setError('');
            }, 3000);
        }
    }, [error, fileName]);


    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        const fun = 'handleDrop:';
        try {
            e.preventDefault();
            setIsDragging(false);
            const file = e.dataTransfer.files[0];
            validateAndSelectFile(e, file);
        } catch (error) {
            setFileName('');
            setError('Drop failed, please try again!');
            logErr(fun, error);
            triggerSelectCallback();
        }
    };

    const handleFileSelect = (e) => {
        const fun = 'handleFileSelect:';
        try {
            const file = e.target.files[0];
            log(fun, 'file:', file);
            validateAndSelectFile(e, file);
        } catch (error) {
            setFileName('');
            setError('Selection failed, please try again!');
            logErr(fun, error);
            triggerSelectCallback();
        }
    };

    const validateAndSelectFile = (event, file) => {
        const fun = 'validateAndSelectFile:';
        log(fun, 'file:', file, 'event:', event);
        // setError('');
        // setFileName('');
        if (checkNullStr(file)) {
            log(fun, 'file-found-true');
            const fileExt = `.${(file.name + '').split('.').pop().toLowerCase().trim()}`;
            log(fun, 'fileExt:', fileExt, 'allowedExtensions:', allowedExtensions);
            if (allowedExtensions.includes(fileExt)) {
                log(fun, 'fileExt-allowed-true');
                setError('');
                setFileName(file.name + '');
                triggerSelectCallback(event, file, file.name);
            } else {
                log(fun, 'fileExt-allowed-false');
                setError(`Invalid file type. Allowed types: ${allowedExtensions.join(', ')}`);
                setFileName('');
                triggerSelectCallback();
            }
        } else {
            log(fun, 'file-found-false');
            setError(`No file selected!`);
            setFileName('');
            triggerSelectCallback();
        }
    };
    const triggerSelectCallback = (event, file, fileName) => {
        if (onFileSelect) {
            const fileExt = `.${(fileName + '').split('.').pop().toLowerCase().trim()}`;
            onFileSelect(event, file, fileName, fileExt);
        }
    }

    return (<div
        className={`rounded-3 ${className} transition-sm`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
            try {
                fileChooserInputRef.current.click();
            } catch (e) {
                logErr(e);
            }
        }}
        style={{
            border: `2px dashed ${isDragging ? hoverBorderColor : borderColor}`,
            backgroundColor: isDragging ? hoverBgColor : bgColor,
            padding: '20px',
            textAlign: 'center',
            cursor: 'pointer',
        }}
    >
        <input
            className={'d-none'}
            ref={fileChooserInputRef}
            type="file"
            accept={checkNullArr(allowedExtensions) ? allowedExtensions.join(', ') : ''}
            onChange={handleFileSelect}
        />
        {/*<p>{label}</p>*/}
        <Label className={'fs-lg fw-bold select-none w-100 h-100 al-center d-flex cursor-pointer'} widthClass={'100'} color={labelColor}>
            {label}
        </Label>
        {(checkNullStr(fileName) || checkNullStr(error)) && <Label className={'fs-md w-100 jc-center d-flex'}
                                                                   widthClass={'100'} color={checkNullStr(error) ? 'red' : fileNameLabelColor}
                                                                   onClick={(e) => e.preventDefault()}>
            {checkNullStr(error) ? error : fileName}
        </Label>}
    </div>);


    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});

FileDropZone.propTypes = {
    allowedExtensions: PropTypes.arrayOf(PropTypes.string).isRequired,
    label: PropTypes.string,
    onFileSelect: PropTypes.func.isRequired,
    bgColor: PropTypes.string,
    borderColor: PropTypes.string,
    hoverBgColor: PropTypes.string,
    hoverBorderColor: PropTypes.string,
    labelColor: PropTypes.string,
};


export default FileDropZone;
